import React, { Fragment } from "react";
import classNames from "classnames";

import type { FullDatoImage, SponsorFull } from "lib/types/datoBlocks";
import { getDateFormattedNumeric } from "lib/utils";
import BlurUpImage from "components/blurUpImage/blurUpImage";
import Button from "components/buttonNew/button";
import FeatureCardLegacy from "components/featureCard/featureCard";
import Image from "../imageNew/image";

export type FeaturedHorizontalItemProps = {
  backgroundColour?: "grey" | "white";
  accentColor?: "pro-blue" | "cerise";
  title: string;
  excerpt?: string;
  featuredLabel?: string;
  publishTimestamp?: number;
  featuredHeading?: string;
  featuredDescription?: string;
  featuredButtonLabel: string;
  url: string;
  image?: FullDatoImage & {
    sizes?: string;
  };
  onFeaturedButtonClick: () => void;
  onFeatureCardClick: () => void;
  leftLabel?: string;
  rightLabel?: string;
  isSponsored?: boolean;
  sponsorInfo?: SponsorFull[];
};

const FeaturedHorizontalItem = ({
  backgroundColour = "grey",
  accentColor = "pro-blue",
  image,
  url,
  featuredLabel,
  publishTimestamp,
  featuredHeading,
  title,
  featuredDescription,
  featuredButtonLabel,
  excerpt,
  leftLabel,
  onFeaturedButtonClick,
  onFeatureCardClick,
  isSponsored = false,
  sponsorInfo,
}: FeaturedHorizontalItemProps) => {
  return (
    <div
      className={classNames(
        "flex min-h-[256px] flex-grow flex-col  md:flex-row",
        {
          "bg-gray-100": backgroundColour === "grey",
          "bg-gray-100 md:bg-white": backgroundColour === "white",
        }
      )}
    >
      <div className={"flex h-full w-full md:min-h-[256px]"}>
        {image && (
          <div
            className={
              "hidden min-h-[256px] md:block md:max-w-[210px] lg:min-w-[171px] lg:max-w-[171px]"
            }
          >
            <BlurUpImage
              src={image.url}
              alt={image.alt}
              width={image.width}
              height={image.height}
              blurDataURL={image.blurUpThumb || undefined}
              className={"pointer-events-none h-100 w-100 object-cover"}
              sizes={"300w"}
            />
          </div>
        )}
        <div className={"flex-1 px-[30px] py-5 md:min-h-[256px]"}>
          <div className={"flex h-full flex-col lg:flex-row"}>
            <div className={"flex flex-col justify-between"}>
              <div className={"flex-1 text-center md:text-left"}>
                <div className={"mb-3 flex flex-col items-center md:flex-row"}>
                  {featuredLabel && (
                    <p
                      className={classNames(
                        "mt-[6px] inline-flex font-bold text-[14px] leading-[130%] md:mr-5 md:mt-0",
                        {
                          "text-pro-blue": accentColor === "pro-blue",
                          "text-cerise": accentColor === "cerise",
                        }
                      )}
                    >
                      {featuredLabel}
                    </p>
                  )}
                  {publishTimestamp && (
                    <p
                      className={
                        "inline-flex text-[14px] leading-[130%] text-mono-40"
                      }
                    >
                      {getDateFormattedNumeric(publishTimestamp)}
                    </p>
                  )}
                </div>
                <h2
                  className={
                    "mb-[10px] line-clamp-3 font-recklessBold text-[32px] leading-[130%] text-black-rock"
                  }
                >
                  {featuredHeading || title}
                </h2>
                <p
                  className={
                    "line-clamp-4 text-[16px] leading-[130%] text-mono-70 md:line-clamp-2 lg:line-clamp-4"
                  }
                >
                  {featuredDescription || excerpt}
                </p>
              </div>
              {isSponsored && sponsorInfo && sponsorInfo.length > 0 && (
                <div className="mt-4 hidden gap-x-[14px] md:flex">
                  {sponsorInfo.map((sponsor, i) => (
                    <Fragment key={i}>
                      {sponsor.sponsorshipType && (
                        <div key={i} className={"flex flex-col"}>
                          <p className="mb-1 text-[12px] leading-[130%] text-mono-70">
                            {sponsor.sponsorshipType}
                          </p>
                          {sponsor.logo && (
                            <div className={"h-[36px] w-[90px] min-w-[90px]"}>
                              <Image
                                src={sponsor.logo.url}
                                alt={sponsor.logo.alt || `${sponsor.name} logo`}
                                width={sponsor.logo.width}
                                height={sponsor.logo.height}
                                blurDataURL={
                                  sponsor.logo.blurUpThumb || undefined
                                }
                                className="pointer-events-none h-100 w-100 object-contain object-center"
                                sizes={"200px"}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
            <div
              className={
                "ml-0 mt-[18px] hidden items-center md:inline-flex lg:ml-[30px] lg:mt-0"
              }
            >
              <Button
                size={"medium"}
                component={"link"}
                href={url}
                colour={"black"}
                onClick={onFeaturedButtonClick}
              >
                {featuredButtonLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div
        data-testid={"featureCard"}
        className={classNames(
          "mx-auto mb-[30px] max-w-[360px] px-5 md:mx-0 md:hidden md:max-w-full",
          {
            "h-[435px]": image?.url,
          }
        )}
      >
        <FeatureCardLegacy
          isSponsored={isSponsored}
          sponsorInfo={sponsorInfo}
          component={"link"}
          href={url}
          title={title}
          wrapperClasses={"h-full"}
          theme={"pro"}
          excerpt={excerpt}
          leftLabel={leftLabel}
          rightLabel={
            publishTimestamp
              ? getDateFormattedNumeric(publishTimestamp)
              : undefined
          }
          backgroundImage={image}
          withPaywall={false}
          onClick={onFeatureCardClick}
        />
      </div>
    </div>
  );
};

export default FeaturedHorizontalItem;
